import { useMemo } from "react";
import BottomSheet from "./BottomSheet";
import { buildInviteUrl } from "../lib/api";
import { toast } from "sonner";
import { Copy } from "lucide-react";

export default function ShareInvitesModal({ open, onClose, members }) {
  const invitees = useMemo(
    () => (members || []).filter((m) => !!m.invite_link_path),
    [members]
  );

  // Left-align invite URLs into readable "name: url" pairs.
  // Pad the "name:" prefix so URLs line up when pasted into a monospaced view.
  const bulkText = useMemo(() => {
    if (invitees.length === 0) return "";
    const maxNameLen = Math.max(...invitees.map((m) => m.display_name.length));
    return invitees
      .map((m) => {
        const pad = " ".repeat(Math.max(0, maxNameLen - m.display_name.length));
        return `${m.display_name}:${pad} ${buildInviteUrl(m.invite_link_path)}`;
      })
      .join("\n");
  }, [invitees]);

  const copyAll = async () => {
    try {
      await navigator.clipboard.writeText(bulkText);
      toast.success("All invite links copied");
    } catch {
      toast.error("Copy failed — long-press the text to copy manually");
    }
  };

  const copyOne = async (m) => {
    try {
      await navigator.clipboard.writeText(buildInviteUrl(m.invite_link_path));
      toast.success(`Copied ${m.display_name}'s link`);
    } catch { toast.error("Copy failed — long-press the URL below to copy manually"); }
  };

  return (
    <BottomSheet open={open} onClose={onClose} title="Share invites" testId="share-invites-modal">
      <div className="space-y-4">
        {invitees.length === 0 ? (
          <div className="bg-[#f8f9fa] rounded-xl p-4 text-center text-sm text-[#6c757d]" data-testid="share-invites-empty">
            No magic-link members yet. Add one from the "Add member" button.
          </div>
        ) : (
          <>
            <button
              onClick={copyAll}
              data-testid="share-invites-copy-all"
              className="w-full min-h-[44px] bg-[#1971c2] hover:bg-[#1864ab] text-white font-display font-extrabold text-base rounded-xl py-3.5 active:scale-95 transition-transform shadow-md flex items-center justify-center gap-2"
            >
              <Copy className="w-5 h-5" strokeWidth={2.5} />
              Copy all invite links
            </button>

            <div className="text-[10px] font-black uppercase tracking-wider text-[#6c757d] px-1">
              Individual links · {invitees.length}
            </div>

            <div className="space-y-2 max-h-[55vh] overflow-y-auto" data-testid="share-invites-list">
              {invitees.map((m) => (
                <div
                  key={m.user_id}
                  className="border border-[#e9ecef] rounded-xl p-3"
                  data-testid={`share-row-${m.user_id}`}
                >
                  <div className="flex items-center gap-2 mb-2">
                    <span className="text-2xl" aria-hidden>{m.avatar_emoji}</span>
                    <div className="flex-1 min-w-0">
                      <div className="font-bold text-sm truncate">{m.display_name}</div>
                      <div className="text-[10px] text-[#6c757d]">
                        {m.approved ? "Approved" : "Pending approval"}
                        {m.is_admin ? " · Admin" : ""}
                      </div>
                    </div>
                  </div>
                  <div
                    className="text-[11px] text-[#212529] font-mono break-all bg-[#f8f9fa] rounded p-2 mb-2 select-all"
                    data-testid={`share-url-${m.user_id}`}
                  >
                    {buildInviteUrl(m.invite_link_path)}
                  </div>
                  <button
                    onClick={() => copyOne(m)}
                    data-testid={`share-copy-${m.user_id}`}
                    className="w-full min-h-[40px] bg-[#f8f9fa] hover:bg-[#e9ecef] text-[#212529] font-bold text-xs rounded-lg py-2 flex items-center justify-center gap-1.5 active:scale-95 transition-transform border border-[#e9ecef]"
                  >
                    <Copy className="w-3.5 h-3.5" strokeWidth={2.5} /> Copy link
                  </button>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </BottomSheet>
  );
}
